import React from 'react';
import { Code2, Database, Layers, Terminal } from 'lucide-react';
import { personalInfo, education } from '../data/mock';
import { Card, CardContent } from './ui/card';

const About = () => {
  const highlights = [
    {
      icon: Code2,
      title: 'Frontend Development',
      description: 'Building responsive and interactive user interfaces with React, HTML, CSS, and JavaScript',
      color: '#10b981'
    },
    {
      icon: Database,
      title: 'Backend Development',
      description: 'Creating robust server-side applications with Python, REST APIs, and database management',
      color: '#06b6d4'
    },
    {
      icon: Layers,
      title: 'Full Stack Solutions',
      description: 'End-to-end application development with modern tech stacks and best practices',
      color: '#f97316'
    },
    {
      icon: Terminal,
      title: 'Problem Solving',
      description: 'Strong foundation in DSA and algorithmic thinking for efficient solutions',
      color: '#10b981'
    }
  ];

  return (
    <section id="about" className="py-20 bg-[#0a0a0b]">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              About <span className="text-[#10b981]">Me</span>
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#10b981] to-[#06b6d4] mx-auto"></div>
          </div>

          {/* Content Grid */}
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            {/* Left: Bio */}
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold text-white mb-4">Get to know me</h3>
                <p className="text-[#a3a3a3] leading-relaxed mb-4">
                  {personalInfo.bio}
                </p>
                <p className="text-[#a3a3a3] leading-relaxed">
                  Currently pursuing my Bachelor's in Technology from NIMS University, I'm constantly exploring new technologies and building projects that solve real-world problems. My goal is to create scalable, efficient, and user-friendly applications that make a difference.
                </p>
              </div>

              <div className="flex flex-wrap gap-3">
                <span className="px-4 py-2 rounded-lg bg-[#121214] border border-[#10b981] text-[#10b981] text-sm">Open to Opportunities</span>
                <span className="px-4 py-2 rounded-lg bg-[#121214] border border-[#06b6d4] text-[#06b6d4] text-sm">Quick Learner</span>
                <span className="px-4 py-2 rounded-lg bg-[#121214] border border-[#f97316] text-[#f97316] text-sm">Team Player</span>
              </div>
            </div>

            {/* Right: Highlights */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {highlights.map((item, index) => {
                const Icon = item.icon;
                return (
                  <Card
                    key={index}
                    className="bg-[#121214] border-[#1f1f23] hover:border-[#10b981] transition-all duration-300 hover:scale-105 group"
                  >
                    <CardContent className="p-6 space-y-3">
                      <div
                        className="w-12 h-12 rounded-lg flex items-center justify-center bg-[#0a0a0b] group-hover:scale-110 transition-transform duration-300"
                        style={{ color: item.color }}
                      >
                        <Icon size={24} />
                      </div>
                      <h4 className="text-white font-semibold">{item.title}</h4>
                      <p className="text-[#a3a3a3] text-sm">{item.description}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>

          {/* Education Timeline */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-8 text-center">Education</h3>
            <div className="space-y-6">
              {education.map((edu, index) => (
                <Card
                  key={edu.id}
                  className="bg-[#121214] border-[#1f1f23] hover:border-[#06b6d4] transition-all duration-300"
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      <div className="flex-1">
                        <h4 className="text-xl font-semibold text-white mb-2">{edu.degree}</h4>
                        <p className="text-[#10b981] mb-1">{edu.institution}</p>
                        <p className="text-[#a3a3a3] text-sm">{edu.location}</p>
                        {edu.description && (
                          <p className="text-[#a3a3a3] text-sm mt-2">{edu.description}</p>
                        )}
                      </div>
                      <div className="flex-shrink-0">
                        <span className="px-4 py-2 rounded-lg bg-[#0a0a0b] border border-[#1f1f23] text-[#06b6d4] text-sm font-mono">
                          {edu.duration}
                        </span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
